import React from 'react';
import { Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  PRESERVE_LINE_BREAKS_SX,
  SCROLLABLE_DESCRIPTION_SX,
  SCROLLABLE_MENU_LIST_SX,
  formatMultilineDisplayText,
} from '../ManageEvent/eventFormConstants';
import ConfirmationDetailField from '../ManageEvent/components/ConfirmationDetailField';
import ConfirmationStatusBadge from '../ManageEvent/components/ConfirmationStatusBadge';
import AttendeeEligibilityBadge from '../../shared/registration/AttendeeEligibilityBadge';
// import RequireJoinApprovalBadge from '../../shared/registration/RequireJoinApprovalBadge';
import ConfirmationEventTopicsSection from '../ManageEvent/components/ConfirmationEventTopicsSection';
// import ConfirmationMemberMatchingSection from '../ManageEvent/components/ConfirmationMemberMatchingSection';
import MenuDishImageThumbnail from './MenuDishImageThumbnail';
import ScrollableContentWithHint from './ScrollableContentWithHint';

const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1rem',
  marginBottom: 12,
});

const MenuRow = ({ item }) => {
  const dishDescription = formatMultilineDisplayText(item.description);

  return (
    <Box sx={{ mt: !item.showOrDivider ? 5 : 0 }}>
      {item.showOrDivider && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontStyle: 'italic', mb: 1, pl: 0.5 }}
        >
          or
        </Typography>
      )}
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
        <MenuDishImageThumbnail src={item.imageUrl} borderRadius={2} />
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            {item.course}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.5 }}>
            {(item.dishName || item.name || '—').trim() || '—'}
          </Typography>
          {dishDescription ? (
            <Typography
              component="div"
              variant="body2"
              color="text.secondary"
              sx={{ lineHeight: 1.5, mt: 0.25, ...PRESERVE_LINE_BREAKS_SX }}
            >
              {dishDescription}
            </Typography>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

const hasMenuItems = (setMenu = []) =>
  setMenu.some(
    (item) =>
      Boolean((item.dishName || '').trim()) ||
      Boolean(formatMultilineDisplayText(item.description)),
  );

const EventDetailsContent = ({ event }) => (
  <Grid container spacing={4} sx={{ pt: 3 }}>
    <Grid item xs={12} md={7}>
      <SectionTitle>Description</SectionTitle>
      <ScrollableContentWithHint sx={{ mb: 3, ...SCROLLABLE_DESCRIPTION_SX }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ lineHeight: 1.7, ...PRESERVE_LINE_BREAKS_SX }}
        >
          {event.description}
        </Typography>
      </ScrollableContentWithHint>

      <SectionTitle>Short Description</SectionTitle>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, mb: 3 }}>
        {event.wingmanDescription}
      </Typography>

      <SectionTitle>Event Settings</SectionTitle>
      <Box sx={{ mb: 3 }}>
        <ConfirmationDetailField variant="row" label="Enable Waitlist">
          <ConfirmationStatusBadge enabled={event.enableWaitlist} />
        </ConfirmationDetailField>
        {/* Refund Policy hidden until shown in the product UI. */}
        {/* {event.refundPolicyLabel ? (
          <ConfirmationDetailField
            variant="row"
            label="Refund Policy"
            value={event.refundPolicyLabel}
          />
        ) : null} */}
        <ConfirmationDetailField variant="row" label="Gender Access">
          <AttendeeEligibilityBadge value={event.attendeeEligibility} />
        </ConfirmationDetailField>
        {/* Approval Flow hidden until users can configure this in the form. */}
        {/* <ConfirmationDetailField variant="row" label="Approval Flow">
          <RequireJoinApprovalBadge required={event.requireJoinApproval} />
        </ConfirmationDetailField> */}

        <ConfirmationEventTopicsSection topics={event.topics} />
        {/* Member Matching hidden until users can configure this in the form. */}
        {/* <ConfirmationMemberMatchingSection enabled={event.memberMatching} /> */}
      </Box>
    </Grid>

    <Grid item xs={12} md={5}>
      <SectionTitle>Menu Items:</SectionTitle>
      {hasMenuItems(event.setMenu) ? (
        <ScrollableContentWithHint sx={SCROLLABLE_MENU_LIST_SX}>
          {event.setMenu.map((item) => (
            <MenuRow key={`${item.course}-${item.imageSeed}`} item={item} />
          ))}
        </ScrollableContentWithHint>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No menu items added.
        </Typography>
      )}
    </Grid>
  </Grid>
);

export default EventDetailsContent;
